import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Scissors, ArrowRight, Sparkles, Zap, Shield } from "lucide-react";

export default function MarketingSitePage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden relative" data-testid="marketing-site">
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-rose-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-violet-500/10 blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] rounded-full bg-teal-500/5 blur-3xl" />
      </div>

      {/* Nav */}
      <nav className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center">
            <Scissors className="w-4.5 h-4.5 text-white" strokeWidth={2.25} />
          </div>
          <span className="font-['Outfit'] text-lg font-semibold tracking-tight">SalonFlow OS</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" className="hidden sm:inline-flex items-center text-white/60 hover:text-white text-sm font-['Manrope'] px-4 py-2 rounded-full hover:bg-white/5 transition">
            Sign in
          </Link>
          <Link to="/signup" data-testid="marketing-cta-signup">
            <Button className="rounded-full h-10 px-5 bg-white text-slate-900 hover:bg-slate-100 font-semibold text-sm">
              Start free trial <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-24 text-center">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-7">
          <Sparkles className="w-3 h-3 text-rose-300" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-white/70 font-['Manrope']">
            Multi-workspace SaaS platform
          </span>
        </div>
        <h1 className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02]">
          One operating system <br className="hidden sm:block" />
          for your entire <span className="bg-gradient-to-r from-rose-400 via-violet-400 to-teal-300 text-transparent bg-clip-text">salon empire</span>.
        </h1>
        <p className="text-white/50 font-['Manrope'] text-lg max-w-2xl mx-auto mt-6 leading-relaxed">
          Scheduling, POS, CRM, marketing, payroll, inventory — in one premium interface.
          Spin up your own isolated workspace in 60 seconds. Full team access.
          Built for multi-location beauty brands.
        </p>
        <div className="flex items-center justify-center gap-3 mt-10">
          <Link to="/signup">
            <Button className="rounded-full h-12 px-7 bg-gradient-to-r from-rose-500 to-violet-500 hover:from-rose-600 hover:to-violet-600 text-white font-semibold text-base" data-testid="hero-cta-signup">
              Create your workspace <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="rounded-full h-12 px-7 bg-transparent border-white/20 text-white hover:bg-white/10 font-semibold text-base">
              Sign in
            </Button>
          </Link>
        </div>
        <p className="text-white/30 text-xs mt-6 font-['Manrope']">14-day free trial · No credit card · Cancel anytime</p>
      </section>

      {/* Feature grid */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Zap, label: "Lightning Calendar", desc: "Drag-drop scheduling with live conflict detection, no-show risk scoring, and AI slot suggestions.", color: "from-rose-500/20 to-rose-500/0" },
            { icon: Shield, label: "Private by Default", desc: "Every workspace is hard-isolated at the database layer. Your clients, revenue and staff stay yours alone.", color: "from-violet-500/20 to-violet-500/0" },
            { icon: Sparkles, label: "AI-First Ops", desc: "Marketing copy, client scoring, rebooking nudges, shift briefings — all powered by GPT-5.2.", color: "from-teal-500/20 to-teal-500/0" },
          ].map((f, i) => (
            <div key={i} className={`relative group rounded-3xl p-7 bg-gradient-to-br ${f.color} border border-white/10 backdrop-blur-sm hover:border-white/20 transition-all hover:-translate-y-0.5`}>
              <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <f.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold mb-1.5">{f.label}</h3>
              <p className="text-white/50 font-['Manrope'] text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social proof strip */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 pb-24 text-center">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/30 font-['Manrope'] mb-8">Trusted by salons, spas, and med-spa groups</p>
        <div className="flex items-center justify-center gap-8 sm:gap-16 opacity-40 flex-wrap">
          {["Julianna Salon Group", "Luxe Hair Studio", "Glow Skin Bar", "Polished Nail Lounge"].map(n => (
            <span key={n} className="font-['Outfit'] text-lg sm:text-xl font-medium tracking-tight">{n}</span>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between flex-wrap gap-3">
          <p className="text-white/30 text-xs font-['Manrope']">© 2026 SalonFlow OS</p>
          <div className="flex items-center gap-5 text-xs text-white/40 font-['Manrope']">
            <Link to="/signup" className="hover:text-white">Sign up</Link>
            <Link to="/login" className="hover:text-white">Sign in</Link>
            <Link to="/platform/login" className="hover:text-white">Platform admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
